import { useEffect, useState } from 'react';
import { Icons } from '~/components/icons';
import { Button, Card, CardBody } from '~/components/ui';
import { API_ENDPOINTS } from '~/constant/api-endpoints';
import { useAuth, useCrud, useModal } from '~/hooks';
import { cn } from '~/lib/utils';
import { formatCurrency, formatNumber } from '~/utils/formater';
import { PaymentQR } from './payment-qr';
import { RenewPackageModal } from './renew-package-modal';
import { ROLES } from '~/constant';

type BillingCycle = '12' | '6';

export const PricingCards = () => {
	const [billingCycle, setBillingCycle] = useState<BillingCycle>('12');
	const { user } = useAuth();
	const { id_product = 0 } = user || {};
	const { openDetailModal, closeModal } = useModal();
	const { getAll } = useCrud([API_ENDPOINTS.dic.product], {
		endpoint: ROLES.ROOT,
	}, {
		staleTime: 0,
	});

	const { data: responseData, isFetching }: any = getAll();
	const rawData = responseData?.data ?? responseData;
	const canSelectDuration = rawData?.can_select_duration ?? true;
	const durationMonths = rawData?.duration_months;
	const productList: any[] = Array.isArray(rawData)
		? rawData
		: Array.isArray(rawData?.list_product)
			? rawData.list_product
			: [];

	useEffect(() => {
		if (durationMonths != null) {
			setBillingCycle(Number(durationMonths) === 6 ? '6' : '12');
		}
	}, [durationMonths]);

	if (isFetching) {
		return (
			<div className="flex justify-center items-center h-64">
				<Icons.loader className="animate-spin text-primary" size={40} />
			</div>
		);
	}

	return (
		<div className="space-y-4 container mx-auto mt-5">
			{/* Top Header & Billing Cycle Toggle */}
			<div className="flex flex-col md:flex-row md:items-center justify-between gap-4 px-2 xl:px-4">
				<div>
					<h2 className="text-xl xl:text-2xl font-bold text-orange-600">
						Nâng cấp gói dịch vụ
					</h2>
					<p className="text-xs xl:text-sm text-gray-500 mt-1">
						Chọn gói phù hợp với quy mô đội ngũ của bạn. Tất cả các gói đều bao gồm các tính năng cơ bản của TRIAL.
					</p>
				</div>
				<div className="flex justify-end pt-3">
					<div className="relative flex items-center bg-white rounded-full p-1 border border-gray-200">
						<span className="absolute -top-5 left-2 bg-secondary text-white text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap shadow-sm">
							Tiết kiệm 20%
						</span>
						<button
							type="button"
							disabled={!canSelectDuration}
							onClick={() => {
								if (canSelectDuration) setBillingCycle('12');
							}}
							className={cn(
								'px-4 md:px-10 py-1.5 rounded-full text-xs xl:text-sm font-semibold transition-all duration-200',
								billingCycle === '12'
									? 'bg-secondary/20 text-secondary shadow-sm'
									: 'text-gray-600 hover:text-gray-900 bg-white',
								!canSelectDuration && (billingCycle === '12' ? 'cursor-default' : 'text-gray-300 hover:text-gray-300 cursor-not-allowed'),
							)}
						>
							12 tháng
						</button>
						<button
							type="button"
							disabled={!canSelectDuration}
							onClick={() => {
								if (canSelectDuration) setBillingCycle('6');
							}}
							className={cn(
								'px-4 md:px-10 py-1.5 rounded-full text-xs xl:text-sm font-semibold transition-all duration-200',
								billingCycle === '6'
									? 'text-secondary shadow-sm bg-secondary/20'
									: 'text-gray-600 hover:text-gray-900 bg-white',
								!canSelectDuration && (billingCycle === '6' ? 'cursor-default' : 'cursor-not-allowed'),
							)}
						>
							6 tháng
						</button>
					</div>
				</div>
			</div>

			{/* Product Cards */}
			<div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 p-2 xl:p-4 gap-1">
				{productList?.map((product: any) => {
					const productCode = product.product_code?.toUpperCase();
					const isGrowth = productCode === 'GROWTH';
					const subtitle = product.product_subtitle || '';
					const contentTitle = product.product_content_title || '';
					const features = product.product_content?.split('\n').filter(Boolean) || [];
					const isCurrentProduct = id_product === product.id;
					const isLowerProduct = id_product > product.id;
					const displayAmount =
						billingCycle === '12' ? product.amount : product.amount_6_month;

					return (
						<Card
							key={product.id}
							className={cn(
								'relative transition-all duration-300 hover:shadow-xl hover:border-secondary hover:cursor-pointer border-2 flex flex-col h-full bg-white',
								isGrowth ? 'bg-secondary/5 border-secondary' : 'border-transparent',
								isLowerProduct ? 'opacity-60 cursor-not-allowed' : '',
								isCurrentProduct && 'border-green-500',
							)}
						>
							{isGrowth && (
								<div className="absolute top-0 right-10 bg-secondary text-white text-xs font-bold px-3 py-0.5 rounded-b-md whitespace-nowrap z-20">
									Phổ biến
								</div>
							)}
							<CardBody className="p-3 xl:p-6 flex flex-col h-full">
								{/* Header */}
								<div className="mb-2 xl:mb-4">
									<h3
										className={cn(
											'tex-base xl:text-lg font-bold mb-1',
											isGrowth
												? 'text-secondary'
												: product.amount === 0
													? 'text-secondary'
													: 'text-gray-900',
										)}
									>
										{product.product_code}
									</h3>
									<span className={`inline-flex items-center justify-center rounded-md px-2 py-1  ${isGrowth ? 'text-secondary bg-secondary/10' : 'text-gray-800 bg-gray-100'} text-[13px] font-medium`}>{subtitle}</span>
								</div>

								{/* Price */}
								<div className="mb-2 xl:mb-4">
									<div className="flex items-baseline gap-1 flex-wrap">
										<span className="text-lg xl:text-2xl font-semibold xl:font-black text-gray-900">
											{formatCurrency(displayAmount).replace('₫', '')}
										</span>
										<span className="text-sm xl:text-lg font-medium xl:font-bold text-gray-900">
											VNĐ
										</span>
									</div>
									<span className="text-gray-400 text-xs">
										/ {billingCycle === '12' ? '12 tháng' : '6 tháng'}
									</span>
								</div>

								{/* Action Button */}
								{product.amount > 0 ? (
									<Button
										color={isGrowth ? 'warning' : 'primary'}
										className={cn(
											'w-full mb-3 xl:mb-6 text-sm xl:text-base font-medium xl:font-bold text-white rounded-md py-2',
											isGrowth ? 'bg-secondary' : 'bg-[#003594]',
											isCurrentProduct && 'bg-green-500',
											isLowerProduct && 'cursor-not-allowed',
										)}
										onClick={() => {
											if (isCurrentProduct) {
												openDetailModal('', {
													title: undefined,
													size: '2xl',
													modalProps: {
														hideCloseButton: true,
														classNames: {
															base: 'p-0 bg-transparent shadow-none border-none max-w-2xl',
															wrapper: 'p-0',
															body: 'p-0',
															closeButton: 'hidden',
														},
													},
													renderContent: () => (
														<RenewPackageModal
															user={user}
															onClose={() => closeModal()}
															onProceedPayment={(renewData) => {
																closeModal();
																setTimeout(() => {
																	openDetailModal(
																		{
																			id: renewData.idProduct || product.id,
																			product_code: renewData.productCode || product.product_code,
																			users_no: user?.users_package,
																			storage: user?.storage_package,
																			display_amount: renewData.totalAmount,
																			billing_cycle: renewData.billingCycle,
																			extra_storage: renewData.extraStorage,
																		},
																		{
																			size: '4xl',
																			modalProps: {
																				hideCloseButton: true,
																				classNames: {
																					base: 'p-0 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden',
																					wrapper: 'p-0',
																					body: 'p-0',
																					closeButton: 'hidden',
																				},
																			},
																			renderContent: ({ data }: any) => (
																				<PaymentQR
																					product={data}
																					user={user}
																					mode="renew"
																					onClose={() => closeModal()}
																				/>
																			),
																		},
																	);
																}, 200);
															}}
														/>
													),
												});
												return;
											}

											openDetailModal(
												{ ...product, billing_cycle: billingCycle, display_amount: displayAmount },
												{
													size: '4xl',
													modalProps: {
														hideCloseButton: true,
														classNames: {
															base: 'p-0 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden',
															wrapper: 'p-0',
															body: 'p-0',
															closeButton: 'hidden',
														},
													},
													renderContent: ({ data }: any) => (
														<PaymentQR
															product={data}
															user={user}
															mode="upgrade"
															onClose={() => closeModal()}
															onSwitchToUpgrade={() => {
																closeModal();
																const element =
																	document.getElementById('pricing-section') || document.body;
																element.scrollIntoView({ behavior: 'smooth' });
															}}
														/>
													),
												},
											);
										}}
										isDisabled={isLowerProduct}
									>
										{isCurrentProduct ? 'Gia hạn gói' : 'Nâng cấp ngay'}
									</Button>
								) : (
									<Button
										color={'secondary'}
										className={cn(
											'w-full mb-3 xl:mb-6 text-sm xl:text-base font-medium xl:font-bold text-white rounded-md py-2 uppercase tracking-wider',
											'bg-secondary',
										)}
									>
										Liên hệ
									</Button>
								)}

								{/* Key Specs */}
								<div className="space-y-1 mb-2 xl:mb-4 text-sm">
									<div className="flex items-center gap-1 xl:gap-3 text-gray-700">
										<Icons.users size={18} className="text-gray-900" />
										<b>{formatNumber(product.users_no)} Sale</b>
									</div>
									<div className="flex items-center gap-1 xl:gap-3 text-gray-700">
										<Icons.database size={18} className="text-gray-900" />
										<b>{formatNumber(product.storage)} GB dung lượng</b>
									</div>
								</div>

								<div className="border-t border-gray-100 my-1 xl:my-2.5"></div>

								{/* Feature List Title */}
								{contentTitle && (
									<p className="text-sm font-bold mb-1.5 xl:mb-2">
										{contentTitle}
									</p>
								)}

								{/* Feature List */}
								<ul className="space-y-1.5 xl:space-y-3 grow">
									{features.map((feature: any, idx: number) => (
										<li key={idx} className="flex items-start gap-1">
											<Icons.check size={16} className="text-green-600 mt-0.5 shrink-0" />
											<span className="text-gray-700 text-[11px] xl:text-[13px] leading-relaxed">
												{feature}
											</span>
										</li>
									))}
								</ul>
							</CardBody>
						</Card>
					);
				})}
			</div>

			{/* Notes Footer */}
			<div className="mx-2 xl:mx-4 p-4 rounded-xl bg-blue-50/60 border border-blue-100 flex items-start gap-3">
				<div className="p-1 rounded-full bg-blue-500 text-white shrink-0 mt-0.5">
					<Icons.info size={18} />
				</div>
				<div className="text-xs xl:text-sm text-gray-700">
					<p className="font-bold text-gray-900 mb-1">Lưu ý:</p>
					<ul className="list-disc list-inside space-y-1 text-gray-600">
						<li>Giá dịch vụ được hiển thị theo kỳ thanh toán 6 hoặc 12 tháng.</li>
						<li>Nâng cấp gói đang sử dụng: giữ nguyên ngày hết hạn hiện tại.</li>
						<li>Để mua thêm Sale hoặc dung lượng, hãy nhấn vào thông tin hạn mức tương ứng trên thanh phía trên.</li>
					</ul>
				</div>
			</div>
		</div>
	);
};
